import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {RefObject} from 'react';

import {pluralize} from 'BenchlingUtil';
import {GRAY_DARKER, BLUE, GRAY} from 'ui-library/Colors';

interface PlateMapWellSelectionSectionProps {
  selectedWellIndices: Set<number>;
  paragraphRef?: RefObject<HTMLParagraphElement>;
  setSelectedWellIndices: (selectedWellIndices: Set<number>) => void;
  totalWells: number;
}

/**
 * Renders a count of the selected wells and a "Select all | Deselect all" text.
 */
export function PlateMapWellSelectionSection({
  selectedWellIndices,
  paragraphRef,
  setSelectedWellIndices,
  totalWells,
}: PlateMapWellSelectionSectionProps): JSX.Element {
  const isSomeSelected = selectedWellIndices.size > 0;
  const isAllSelected = selectedWellIndices.size === totalWells;

  function handleClickSelectAll(): void {
    setSelectedWellIndices(new Set(Array(totalWells).keys()));
  }

  function handleClickDeselectAll(): void {
    setSelectedWellIndices(new Set());
  }

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'PlateMapWellSelectionSection', element: 'root'})}
    >
      <p className={css(styles.numSelected)} ref={paragraphRef}>
        {`${selectedWellIndices.size} ${pluralize(selectedWellIndices.size, 'well')} selected`}
      </p>
      <div>
        <a
          className={classNames(
            css(styles.selectionModeText),
            isAllSelected ? css(styles.disabledSelectionMode) : css(styles.enabledSelectionMode)
          )}
          aria-disabled={isAllSelected}
          onClick={handleClickSelectAll}
        >
          Select all
        </a>
        <span className="u-small-padding-left u-small-padding-right">|</span>
        <a
          className={classNames(
            css(styles.selectionModeText),
            isSomeSelected ? css(styles.enabledSelectionMode) : css(styles.disabledSelectionMode)
          )}
          aria-disabled={!isSomeSelected}
          onClick={handleClickDeselectAll}
        >
          Deselect all
        </a>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 16,
  },
  numSelected: {
    color: GRAY_DARKER,
    margin: 'auto 8px auto 0',
  },
  selectionModeText: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  enabledSelectionMode: {
    color: BLUE,
  },
  disabledSelectionMode: {
    color: GRAY,
    pointerEvents: 'none',
    cursor: 'default',
  },
});
